import { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import {
  HeaderTitleContext,
  useCanClaimHeaderTitle,
  useHeaderTitleSlot,
} from './header-title.hooks';

export function HeaderTitleProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [claims, setClaims] = useState<Record<string, number>>({});
  const [slots, setSlots] = useState<Record<string, HTMLElement>>({});

  // Callback identities stay stable without useCallback: the React Compiler
  // memoizes them, and consumers' effects and ref callbacks depend on that.
  function claim(anchor: string) {
    setClaims((prev) => ({ ...prev, [anchor]: (prev[anchor] ?? 0) + 1 }));
    return () =>
      setClaims((prev) => {
        const next = { ...prev };
        const count = (next[anchor] ?? 0) - 1;
        if (count > 0) next[anchor] = count;
        else delete next[anchor];
        return next;
      });
  }

  function setSlot(anchor: string, element: HTMLElement | null) {
    setSlots((prev) => {
      if (element === null) {
        if (!(anchor in prev)) return prev;
        const next = { ...prev };
        delete next[anchor];
        return next;
      }
      if (prev[anchor] === element) return prev;
      return { ...prev, [anchor]: element };
    });
  }

  const value = { claims, claim, slots, setSlot };

  return (
    <HeaderTitleContext.Provider value={value}>
      {children}
    </HeaderTitleContext.Provider>
  );
}

/** The element a claimed crumb renders; the claimant's title portals into it. */
export function HeaderTitleSlot({
  anchor,
  current,
}: {
  anchor: string;
  /** Last crumb in the trail — a claimed layout crumb can have crumbs after it. */
  current: boolean;
}) {
  const setSlot = useContext(HeaderTitleContext)?.setSlot;
  return (
    <span
      ref={(element) => setSlot?.(anchor, element)}
      aria-current={current ? 'page' : undefined}
      className={cn(
        'flex min-w-0 max-w-[min(28rem,45vw)] items-center truncate',
        current ? 'font-medium text-foreground' : 'text-muted-foreground',
      )}
    />
  );
}

type HeaderTitleProps = {
  children: React.ReactNode;
  routeId?: string;
  fallback?: React.ReactNode;
};

/**
 * Render `children` as the header crumb for the current route (or `routeId`).
 * `fallback` renders in place when there is no header to render into.
 */
export function HeaderTitle({ fallback = null, ...props }: HeaderTitleProps) {
  if (!useCanClaimHeaderTitle()) return fallback;
  return <ClaimedHeaderTitle {...props} fallback={fallback} />;
}

function ClaimedHeaderTitle({ children, routeId, fallback }: HeaderTitleProps) {
  const slot = useHeaderTitleSlot(true, routeId);
  if (slot === undefined) return fallback;
  return slot ? createPortal(children, slot) : null;
}

/**
 * A page's h1, rendered as its header crumb. The drop-in replacement for a
 * page-level `<h1>` that restates the crumb; renders the old inline heading
 * where there is no header (tests, stories).
 */
export function HeaderPageTitle({
  children,
  routeId,
}: {
  children: React.ReactNode;
  routeId?: string;
}) {
  return (
    <HeaderTitle
      routeId={routeId}
      fallback={
        <h1 className="text-2xl font-bold tracking-tight">{children}</h1>
      }
    >
      <h1 className="truncate">{children}</h1>
    </HeaderTitle>
  );
}
